import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTrafficPattern } from '@/services/traffic-pattern.service.js';
import UrgencyDot from '@/components/shared/UrgencyDot.jsx';

// Persistent signal band — Traffic Pattern condensed (TRACON-DESIGN-SYSTEM.md).
// Always visible across every view; the full /traffic page is its expansion.
// Light 60s poll, paused while the tab is hidden. Four-level urgency only.

const POLL_MS = 60_000;
const URGENT = new Set(['urgent', 'CRITICAL', 'EXPIRED']);
const WARN = new Set(['warn', 'URGENT']);

function severityRank(s) {
  if (URGENT.has(s)) return 0;
  if (WARN.has(s)) return 1;
  return 2;
}

function reasonFor(item) {
  if (item.deal_aircraft?.[0]?.tail_number) return item.deal_aircraft[0].tail_number;
  if (item.counterparty_name) return item.counterparty_name;
  if (item.deal_premium) return `$${Number(item.deal_premium).toLocaleString()}`;
  return null;
}

export default function TodayStrip() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const aborted = useRef(false);

  useEffect(() => {
    aborted.current = false;
    async function load({ poll = false } = {}) {
      if (document.visibilityState !== 'visible') return;
      try {
        const d = await getTrafficPattern({ poll });
        if (!aborted.current) setData(d);
      } catch {
        /* non-fatal — the strip just stays on its last good state */
      }
    }
    load();
    function start() {
      if (!timer.current) timer.current = setInterval(() => load({ poll: true }), POLL_MS);
    }
    function stop() {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    }
    function onVis() {
      if (document.visibilityState === 'visible') {
        load();
        start();
      } else {
        stop();
      }
    }
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      aborted.current = true;
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const items = (data?.groups || [])
    .flatMap((g) => g.items || [])
    .filter((it) => severityRank(it.current_severity) < 2)
    .sort((a, b) => severityRank(a.current_severity) - severityRank(b.current_severity))
    .slice(0, 6);

  const counts = data?.counts || {};
  const todayCount = (counts.overdue || 0) + (counts.today || 0);

  function openItem(it) {
    const acct = it.account_id || it.deal_id;
    if (acct && it.deal_id) {
      navigate(`/account/${encodeURIComponent(acct)}/deal/${encodeURIComponent(it.deal_id)}`);
    } else {
      navigate('/traffic');
    }
  }

  return (
    <div className="flex h-today-h shrink-0 items-center gap-3 overflow-x-auto overflow-y-hidden border-b border-border bg-surface px-3.5">
      <button
        type="button"
        onClick={() => navigate('/traffic')}
        className="flex shrink-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-dim hover:text-text-muted"
      >
        Today
        {todayCount > 0 && (
          <span className="rounded-pill bg-accent px-1.5 text-[10px] font-bold leading-[14px] text-white">
            {todayCount}
          </span>
        )}
      </button>

      {items.length === 0 ? (
        <span className="text-[11.5px] text-text-dim">No open work right now.</span>
      ) : (
        items.map((it) => {
          const reason = reasonFor(it);
          return (
            <button
              key={it.id || `${it.deal_id}:${it.prompt_template_key}`}
              type="button"
              onClick={() => openItem(it)}
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill border border-border bg-surface-strong px-2.5 py-[3px] text-[11.5px] text-text hover:border-border-strong"
            >
              <UrgencyDot urgency={it.current_severity} className="h-[5px] w-[5px]" />
              <span className="max-w-[220px] truncate">{it.deal_account_name || it.subject || 'Task'}</span>
              {reason && <span className="font-mono text-[10.5px] text-text-dim">{reason}</span>}
            </button>
          );
        })
      )}

      <button
        type="button"
        onClick={() => navigate('/traffic')}
        className="ml-auto shrink-0 text-[11px] text-text-muted hover:text-text"
      >
        Week view →
      </button>
    </div>
  );
}
